import React, { useState, useEffect } from "react";
import "./CorporateDashBoard.css";
import {
  Calendar,
  CreditCardIcon,
  Users,
  Building2,
  AlertCircle,
} from "lucide-react";

const CorporateDashboard = () => {
  const [showDashboard, setShowDashboard] = useState(false);
  const [companyCode, setCompanyCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [corporateData, setCorporateData] = useState({});
  const [activeTab, setActiveTab] = useState("employees");

  //  NGO usage state
  const [ngoUsage, setNgoUsage] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(
        `https://backend-xhl4.onrender.com/CorporateRoute/corporate/${companyCode}`
      );

      if (response.ok) {
        const data = await response.json();
        console.log(data);

        const totalEmployees = data.associatedPatients.length;
        const bookedAppointments = data.associatedPatients.reduce(
          (t, emp) => t + emp.visits.length,
          0
        );

        setCorporateData({ ...data, totalEmployees, bookedAppointments });
        setShowDashboard(true);

        // ----------- NGO Logic Start -----------
        if (data.type === "ngo") {
          setActiveTab("recharge"); // default open other tab, no employees

          const ngoRes = await fetch(
            `https://backend-xhl4.onrender.com/CorporateRoute/ngo-usage/${companyCode}`
          );

          const ngoData = await ngoRes.json();
          setNgoUsage(ngoData.usedAppointments || []);
        }
        // ----------- NGO Logic End -------------
      } else setError("Company doesn't exist.");
    } catch {
      setError("Company doesn't exist.");
    }

    setIsLoading(false);
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const formatCurrency = (amt) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amt);

  // ----------------- Login Screen -----------------
  if (!showDashboard)
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-icon">
              <Building2 size={48} />
            </div>
            <h1>Corporate Dashboard</h1>
            <p>Enter company code to access the dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label>Company Code</label>
              <input
                className="form-input"
                required
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value)}
                placeholder="Enter company code"
              />
            </div>

            {error && (
              <div className="error-message">
                <AlertCircle size={16} /> {error}
              </div>
            )}

            <button
              className="submit-button"
              disabled={isLoading || !companyCode.trim()}
            >
              {isLoading ? "Loading..." : "Access Dashboard"}
            </button>
          </form>
        </div>
      </div>
    );

  // ----------------- Dashboard View -----------------
  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1>Corporate Dashboard</h1>
        <div className="company-name">
          <span>
            {corporateData.companyName} ({corporateData.type?.toUpperCase()})
          </span>
        </div>
      </header>

      {/* Cards same as before */}
      <div className="metrics-container">
        <div className="metric-card credits">
          <div className="metric-icon">
            <CreditCardIcon />
          </div>
          <div className="metric-content">
            <h3>Remaining Credits</h3>
            <span className="metric-value">{corporateData.totalCredits}</span>
          </div>
        </div>

        {/* ❗ Hide Employee card for NGO */}
        {corporateData.type !== "ngo" && (
          <div className="metric-card employees">
            <div className="metric-icon">
              <Users />
            </div>
            <div className="metric-content">
              <h3>Total Employees</h3>
              <span className="metric-value">
                {corporateData.totalEmployees}
              </span>
              <p className="metric-subtitle">Registered employees</p>
            </div>
          </div>
        )}

        <div className="metric-card appointments">
          <div className="metric-icon">
            <Calendar />
          </div>
          <div className="metric-content">
            <h3>Booked Appointments</h3>
            <span className="metric-value">
              {corporateData.bookedAppointments}
            </span>
          </div>
        </div>
      </div>

      {/* Doctors SAME */}
      <div className="table-container">
        <h3>Doctors Assigned</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>City</th>
              <th>Gender</th>
              <th>Specialization</th>
              <th>Experience</th>
              <th>Mobile</th>
            </tr>
          </thead>
          <tbody>
            {corporateData.doctors?.length ? (
              corporateData.doctors.map((d) => (
                <tr key={d._id}>
                  <td>{d.Name}</td>
                  <td>{d.City}</td>
                  <td>{d.Gender}</td>
                  <td>{d.areaOfExpertise?.join(", ")}</td>
                  <td>
                    {d.experienceYears}y {d.experienceMonths}m
                  </td>
                  <td>{d.Mobile}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: "center" }}>
                  No doctors assigned
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ---------------- NGO Usage Section ---------------- */}
      {corporateData.type === "ngo" && (
        <div className="table-container">
          <h3>NGO Patients Used Slots</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Patient</th>
                <th>Gender</th>
                <th>Problem</th>
                <th>Phone</th>
                <th>Doctor</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {ngoUsage.length ? (
                ngoUsage.map((a) => (
                  <tr key={a._id}>
                    <td>{a.patient_id?.Name}</td>
                    <td>{a.patient_id?.Gender}</td>
                    <td>{a.patient_id?.Problem?.join(", ")}</td>
                    <td>{a.patient_id?.Mobile}</td>
                    <td>{a.doctor_id?.Name}</td>
                    <td>{formatDate(a.DateOfAppointment)}</td>
                    <td>{a.appointment_status}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    No usage yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tabs Section */}
      <div className="tabs-container">
        <div className="tabs-navigation">
          {/*  Employees Tab hidden for NGO */}
          {corporateData.type !== "ngo" && (
            <button
              className={`tab-button ${activeTab === "employees" && "active"}`}
              onClick={() => setActiveTab("employees")}
            >
              Employees
            </button>
          )}

          <button
            className={`tab-button ${activeTab === "recharge" && "active"}`}
            onClick={() => setActiveTab("recharge")}
          >
            Recharge History
          </button>

          <button
            className={`tab-button ${activeTab === "refunds" && "active"}`}
            onClick={() => setActiveTab("refunds")}
          >
            Refund History
          </button>
        </div>

        <div className="tab-content">
          {/* Employees — hidden if NGO */}
          {activeTab === "employees" && corporateData.type !== "ngo" && (
            <div className="table-container">
              <h3>Associated Employees</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Phone</th>
                    <th>Department</th>
                    <th>Family</th>
                    <th>Visits</th>
                    <th>Last Visit</th>
                  </tr>
                </thead>
                <tbody>
                  {corporateData.associatedPatients.map((emp) => (
                    <tr key={emp._id}>
                      <td>{emp.empId}</td>
                      <td>{emp.employeePhone}</td>
                      <td>{emp.department}</td>
                      <td>{emp.familyMembers.length}</td>
                      <td>{emp.visits.length}</td>
                      <td>
                        {emp.visits.length
                          ? formatDate(emp.visits.at(-1).date)
                          : "No visits"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recharge */}
          {activeTab === "recharge" && (
            <div className="table-container">
              <h3>Recharge History</h3>
              <table className="data-table">
                <tbody>
                  {corporateData.rechargeHistory.map((r) => (
                    <tr key={r._id}>
                      <td>{formatDate(r.date)}</td>
                      <td>+{r.credits}</td>
                      <td>{formatCurrency(r.amount)}</td>
                      <td>
                        <span className="status-badge success">Completed</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "refunds" && (
            <div className="table-container">
              <h3>Refund History</h3>
              <table className="data-table">
                <tbody>
                  {corporateData.refundHistory.map((f) => (
                    <tr key={f._id}>
                      <td>{formatDate(f.date)}</td>
                      <td>+{f.credits}</td>
                      <td>{f.reason}</td>
                      <td>
                        <span className="status-badge processed">
                          Processed
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CorporateDashboard;
